import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	plusCircle,
	refreshCcw_03,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { CustomerSearch } from "~/shared/components/customer-search/customer-search.tsx";
import { TraxCard } from "~/shared/components/trax-card/trax-card.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { VENDOR_MANAGEMENT_ROLES } from "~/shared/constants/page-roles.ts";
import { useAutoResizeColumns } from "~/shared/hooks/use-auto-resize-columns.ts";
import { useColumnSelections } from "~/shared/hooks/use-column-selections.ts";
import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useCustomerSelection } from "~/shared/hooks/use-customer-selection.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useGetVendorUsers } from "~/shared/hooks/use-vendor-users.ts";
import type { TraxUser } from "~/shared/schemas/trax-user-schema.ts";
import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import type { VendorUserFormData } from "./components/add-user-modal/add-user-modal.tsx";
import { AddEditUserModal } from "./components/add-user-modal/add-user-modal.tsx";
import { DeleteUserModal } from "./components/delete-user-modal/index.tsx";
import { MobileVendorManagement } from "./components/mobile-vendor-management.tsx";
import { useAddVendorUser } from "./hooks/use-add-vendor-user.ts";
import { useDeleteVendorUser } from "./hooks/use-delete-vendor-user.ts";
import { useUpdateVendorUser } from "./hooks/use-update-vendor-user.ts";
import { useVendorColumns } from "./hooks/use-vendor-columns.tsx";
import styles from "./index.module.css";

type ActiveModal =
	| { kind: "none" }
	| { kind: "add" }
	| { kind: "edit"; user: TraxUser }
	| { kind: "delete"; user: TraxUser };

const closedModal: ActiveModal = { kind: "none" };

export const VendorManagement = () => {
	const { isInternal } = useDeploymentType();
	const { data: currentUser, isLoading: isUserLoading } =
		useGetCurrentUserInfo(isInternal);
	const isMobile = useIsMobile();

	const currentUserRole = currentUser?.role ?? "";
	const canManageVendors =
		isInternal || VENDOR_MANAGEMENT_ROLES.has(currentUserRole);

	const { activeCustomer, handleSelectCustomer } = useCustomerSelection({
		baseRoute: routes.vendorManagement,
		customerRoute: routes.vendorManagementWithCustomer,
	});

	const customerId = activeCustomer?.id;

	const canFetchVendorUsers = !isInternal || customerId != null;

	const { users, isLoading, isFetching, refreshUsers } = useGetVendorUsers({
		customerId,
		enabled: canFetchVendorUsers,
	});
	const { skip, take, handlePageChange, isLoadingPreference } = useGridState(
		GridIdentifiers["Vendor Management"],
	);
	const { addVendorUser, isPending: isAdding } = useAddVendorUser();
	const { deleteVendorUser, isPending: isDeleting } = useDeleteVendorUser();
	const { updateVendorUser, isPending: isUpdating } = useUpdateVendorUser();

	const [activeModal, setActiveModal] = useState<ActiveModal>(closedModal);
	const closeModal = useCallback(() => {
		setActiveModal(closedModal);
	}, []);

	const handleEdit = useCallback((user: TraxUser) => {
		setActiveModal({ kind: "edit", user });
	}, []);

	const handleDelete = useCallback((user: TraxUser) => {
		setActiveModal({ kind: "delete", user });
	}, []);

	const vendorColumns = useVendorColumns({
		onEdit: handleEdit,
		onDelete: handleDelete,
		canManageVendors,
	});

	const { columns: selectedColumns } = useColumnSelections(
		"vendor",
		vendorColumns,
	);

	const processedColumns = useMemo(
		() => selectedColumns.filter((col) => col.show),
		[selectedColumns],
	);

	const { wrapperRef, distributedColumns } =
		useAutoResizeColumns(processedColumns);

	const sortedUsers = useMemo(
		() =>
			[...users].sort((a, b) =>
				(a.email ?? "").localeCompare(b.email ?? "", undefined, {
					sensitivity: "base",
				}),
			),
		[users],
	);

	const handleAddSubmit = async (data: VendorUserFormData) => {
		if (customerId == null) return;
		try {
			await addVendorUser({ ...data, customerId });
			closeModal();
		} catch {
			// error toast already fired by the mutation's onError;
			// keep the modal open so the user can retry without losing input.
		}
	};

	const handleEditSubmit = async (data: VendorUserFormData) => {
		if (activeModal.kind !== "edit" || customerId == null) return;
		const { user } = activeModal;
		try {
			await updateVendorUser({
				userId: user.userId,
				firstName: data.firstName,
				lastName: data.lastName,
				customerId,
			});
			closeModal();
		} catch {
			// error toast already fired by the mutation's onError;
			// keep the modal open so the user can retry without losing input.
		}
	};

	const handleDeleteConfirm = async () => {
		if (activeModal.kind !== "delete" || customerId == null) return;
		try {
			await deleteVendorUser({
				userId: activeModal.user.userId,
				customerId,
			});
		} finally {
			closeModal();
		}
	};

	return (
		<TraxPageLayout>
			<TraxPageHeader
				hideRepresentativeOnMobile={true}
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Vendor Management"}
			/>
			<div className={styles.infoBanner}>
				Vendor Management is designed for vendor, supplier, or third-party users
				who submit LTL shipments on your company&apos;s behalf and do not
				require access to quoting, tracking, or invoicing.
			</div>
			{isMobile ? (
				<>
					<div className={styles.mobileCustomerSearch}>
						<CustomerSearch
							disabled={false}
							onCustomerChange={handleSelectCustomer}
							selectedCustomer={activeCustomer}
							wrapperStyle={{ width: "100%" }}
						/>
					</div>
					<MobileVendorManagement
						canManageVendors={!!customerId && canManageVendors}
						loading={
							isLoading || isFetching || isUserLoading || isLoadingPreference
						}
						onAdd={() => {
							setActiveModal({ kind: "add" });
						}}
						onDelete={handleDelete}
						onEdit={handleEdit}
						users={sortedUsers}
					/>
				</>
			) : (
				<>
					<div className={styles.headerActions}>
						<CustomerSearch
							disabled={false}
							onCustomerChange={handleSelectCustomer}
							selectedCustomer={activeCustomer}
							wrapperStyle={{ width: "300px" }}
						/>

						<Button
							onClick={refreshUsers}
							svgIcon={refreshCcw_03}
							variant={"tertiary"}
						/>
					</div>
					<TraxCard
						className={styles.vendorCard}
						contentClassName={styles.vendorCardContent}
						title={"Vendor Management"}
					>
						<div ref={wrapperRef} className={styles.gridWrapper}>
							<TraxGrid
								columns={convertToGridColumnProps(distributedColumns)}
								data={sortedUsers}
								dataItemKey={"userId"}
								filterable={true}
								loading={
									isLoading ||
									isFetching ||
									isUserLoading ||
									isLoadingPreference
								}
								onPageChange={handlePageChange}
								pageable={DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS}
								skip={skip}
								sortable={true}
								style={{ height: "100%" }}
								take={take}
							/>
						</div>
						<div className={styles.saveButton}>
							<Button
								disabled={!customerId || !canManageVendors}
								onClick={() => {
									setActiveModal({ kind: "add" });
								}}
								svgIcon={plusCircle}
								variant={"primary"}
							>
								Add User
							</Button>
						</div>
					</TraxCard>
				</>
			)}

			{activeModal.kind === "add" ? (
				<AddEditUserModal
					// currentUserRole={currentUserRole}
					isPending={isAdding}
					onClose={closeModal}
					onSubmit={handleAddSubmit}
				/>
			) : null}

			{activeModal.kind === "edit" ? (
				<AddEditUserModal
					// currentUserRole={currentUserRole}
					isPending={isUpdating}
					onClose={closeModal}
					onSubmit={handleEditSubmit}
					user={activeModal.user}
				/>
			) : null}

			{activeModal.kind === "delete" ? (
				<DeleteUserModal
					isDeleting={isDeleting}
					onCancel={closeModal}
					onConfirm={() => {
						void handleDeleteConfirm();
					}}
					user={activeModal.user}
				/>
			) : null}
		</TraxPageLayout>
	);
};
